import { createContext, useContext, useState, ReactNode } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export interface UnifiedRole {
    role: string; // matches a Mongoose model name (Admin, Reviewer, EditorInChief, ...)
    profileId: string;
    fullName: string | null;
    email: string | null;
}

interface UnifiedUser {
    id: string;
    email: string | null;
    username: string | null;
    fullName: string | null;
}

interface UnifiedAuthContextType {
    user: UnifiedUser | null;
    roles: UnifiedRole[];
    loading: boolean;
    error: string | null;
    login: (identifier: string, password: string) => Promise<void>;
    logout: () => void;
}

const UnifiedAuthContext = createContext<UnifiedAuthContextType>({
    user: null,
    roles: [],
    loading: false,
    error: null,
    login: async () => { },
    logout: () => { }
});

export const useUnifiedAuth = () => useContext(UnifiedAuthContext);

// Where each role's token and profile are kept in this browser. The Editorial
// Desk (utils/deskApi.ts), the admin panel and the reviewer portal each read
// their own.
const ROLE_STORAGE: Record<string, { tokenKey: string; dataKey: string; shape: 'raw' | 'json' }> = {
    Admin: { tokenKey: 'adminToken', dataKey: 'adminUsername', shape: 'raw' },
    Reviewer: { tokenKey: 'reviewerToken', dataKey: 'reviewerInfo', shape: 'json' },
    Editor: { tokenKey: 'editorToken', dataKey: 'editorData', shape: 'json' },
    EditorInChief: { tokenKey: 'editorInChiefToken', dataKey: 'editorInChiefData', shape: 'json' },
    EditorialOffice: { tokenKey: 'editorialOfficeToken', dataKey: 'editorialOfficeData', shape: 'json' }
};

// Keys left in a browser by the five roles the journal has retired. Nothing
// writes them any more; signing in or out clears what an earlier session left.
const RETIRED_STORAGE_KEYS = ['managingEditor', 'associateEditor', 'ethicsQualityEditor', 'editorialAssistant', 'sectionEditor'].flatMap(
    (name) => [`${name}Token`, `${name}Data`]
);

export const ROLE_LABELS: Record<string, string> = {
    Admin: 'Admin',
    Reviewer: 'Reviewer',
    Editor: 'Production',
    EditorInChief: 'Editor-in-Chief',
    EditorialOffice: 'Editorial Office'
};

// Roles the journal no longer has. An account may still list one until an
// administrator removes it; it cannot be given, and it opens nothing.
export const RETIRED_ROLE_LABELS: Record<string, string> = {
    ManagingEditor: 'Managing Editor',
    AssociateEditor: 'Associate Editor',
    SectionEditor: 'Section Editor',
    EthicsQualityEditor: 'Ethics & Quality Editor',
    EditorialAssistant: 'Editorial Assistant'
};

/** The roles that work on the Editorial Desk. */
export const DESK_ROLE_NAMES = ['Admin', 'EditorInChief', 'EditorialOffice', 'Editor'];

function clearAllRoleStorage() {
    Object.values(ROLE_STORAGE).forEach(({ tokenKey, dataKey }) => {
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(dataKey);
    });
    RETIRED_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem('unifiedUser');
    localStorage.removeItem('unifiedRoles');
}

function readStoredUser(): UnifiedUser | null {
    const stored = localStorage.getItem('unifiedUser');
    return stored ? JSON.parse(stored) : null;
}

function readStoredRoles(): UnifiedRole[] {
    const stored = localStorage.getItem('unifiedRoles');
    return stored ? JSON.parse(stored) : [];
}

export const UnifiedAuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<UnifiedUser | null>(readStoredUser);
    const [roles, setRoles] = useState<UnifiedRole[]>(readStoredRoles);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const login = async (identifier: string, password: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post(`${API_URL}/api/login`, { identifier, password });
            const { user: returnedUser, roles: returnedRoles, tokens } = response.data as {
                user: UnifiedUser;
                roles: UnifiedRole[];
                tokens: Record<string, string>;
            };

            // One account at a time in a browser: whatever an earlier session
            // left (another person's roles, a retired role's token) goes first.
            clearAllRoleStorage();

            returnedRoles.forEach((entry) => {
                const config = ROLE_STORAGE[entry.role];
                const token = tokens[entry.role];
                if (!config || !token) return;

                localStorage.setItem(config.tokenKey, token);
                if (config.shape === 'raw') {
                    localStorage.setItem(config.dataKey, returnedUser.username || '');
                } else {
                    localStorage.setItem(config.dataKey, JSON.stringify({
                        _id: entry.profileId,
                        id: entry.profileId,
                        name: entry.fullName,
                        fullName: entry.fullName,
                        email: entry.email
                    }));
                }
            });

            localStorage.setItem('unifiedUser', JSON.stringify(returnedUser));
            localStorage.setItem('unifiedRoles', JSON.stringify(returnedRoles));

            // The login lands where the work is: the Editorial Desk for anyone
            // with an editorial role, otherwise the list of workspaces (a
            // reviewer's portal). A full reload, because the contexts mounted in
            // App.tsx read the stored tokens once, when they mount.
            const onDesk = returnedRoles.some((entry) => DESK_ROLE_NAMES.includes(entry.role));
            window.location.href = onDesk ? '/desk' : '/dashboard';
        } catch (err: any) {
            setError(err.response?.data?.message || 'Login failed. Please try again.');
            setLoading(false);
            throw err;
        }
    };

    const logout = () => {
        clearAllRoleStorage();
        setUser(null);
        setRoles([]);
        window.location.href = '/login';
    };

    return (
        <UnifiedAuthContext.Provider value={{ user, roles, loading, error, login, logout }}>
            {children}
        </UnifiedAuthContext.Provider>
    );
};
